import axios from 'axios';
import { API_ENDPOINTS, getCmsApiBaseUrl } from './api.config';

export interface GetUsersParams {
  page?: number;
  per_page?: number;
  role?: string;
  filter?: string;
}

export interface User {
  id: string;
  user_id: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  nickname?: string;
  picture?: string;
  created_at?: string;
  blocked?: boolean;
  organizations?: string[]; // Array of organization IDs
  app_metadata?: {
    roles?: string[];
    external_permissions?: string[];
  };
}

export interface GetUsersResponse {
  page: number;
  per_page: number;
  total: number;
  users: User[];
}

export interface TeamMember {
  user_id: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  roles?: string[];
}

export interface ProfileOrganization {
  id: string;
  customerId?: string;
  flows?: Array<{ flowId: string; flowName?: string }>;
}

export interface ProfileResponse {
  user_id: string;
  email: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  organizations?: Array<string | ProfileOrganization>;
  roles?: string[];
  /** CMS / RBAC permission strings (may be absent on older APIs). */
  external_permissions?: string[];
  /** Flow IDs assigned to the current user (from profile endpoint) */
  flowids?: string[];
  flow_ids?: string[];
  team_members?: TeamMember[];
}

/** Org IDs from profile that have a customerId (sidebar-visible orgs). */
export function getProfileOrgIdsWithCustomerId(
  profile: Pick<ProfileResponse, 'organizations'> | null | undefined,
): Set<string> | null {
  const orgs = profile?.organizations;
  if (!Array.isArray(orgs) || orgs.length === 0) return null;
  if (typeof orgs[0] === 'string') return null;

  const ids = new Set<string>();
  for (const org of orgs) {
    if (typeof org !== 'object' || !org?.id) continue;
    const customerId = org.customerId?.trim();
    if (customerId) ids.add(org.id);
  }
  return ids;
}

export function getProfileOrganizationId(
  org: string | ProfileOrganization,
): string {
  return typeof org === 'string' ? org : org.id;
}

export function extractProfileFlowIds(
  profile: Pick<ProfileResponse, 'flowids' | 'flow_ids'> | null | undefined,
): string[] {
  if (Array.isArray(profile?.flowids) && profile.flowids.length > 0) {
    return profile.flowids;
  }
  if (Array.isArray(profile?.flow_ids) && profile.flow_ids.length > 0) {
    return profile.flow_ids;
  }
  return [];
}

export interface LoginHistoryLog {
  log_id: string;
  date: string;
  type: string;
  type_name: string;
  client_id?: string;
  client_name?: string;
  ip: string;
  user_agent: string;
  user_id: string;
  user_name: string;
  description?: string;
  connection?: string;
  strategy?: string;
}

export interface GetLoginHistoryParams {
  page?: number;
  limit?: number;
}

export interface GetLoginHistoryResponse {
  logs: LoginHistoryLog[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Get login history for a user
 */
export const getLoginHistory = async (
  userId: string,
  params: GetLoginHistoryParams,
  token: string,
): Promise<GetLoginHistoryResponse> => {
  const { page = 0, limit = 30 } = params;
  const url = `${API_ENDPOINTS.USER_LOGIN_HISTORY(userId)}?page=${page}&limit=${limit}`;
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

/**
 * Get all users with pagination and filters
 */
export const getAllUsers = async (
  params: GetUsersParams = {},
  token: string,
): Promise<GetUsersResponse> => {
  const { page = 0, per_page = 10, role, filter } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });

  if (role) {
    queryParams.append('role', role);
  }

  if (filter) {
    queryParams.append('filter', filter);
  }

  const response = await axios.get(
    `${getCmsApiBaseUrl()}/users?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

/**
 * Get current user's profile with team members
 */
export const getProfile = async (
  token: string,
  liveData?: boolean,
): Promise<ProfileResponse> => {
  const response = await axios.get(`${getCmsApiBaseUrl(liveData)}/profile`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

/**
 * Create a new user
 */
export const createUser = async (
  data: {
    email: string;
    password?: string;
    first_name: string;
    last_name: string;
    organization_ids: string[];
    role_id: string[];
  },
  token: string,
): Promise<User> => {
  const payload = { ...data };
  if (payload.password === undefined || payload.password === '') {
    delete payload.password;
  }
  const response = await axios.post(
    `${getCmsApiBaseUrl()}/user`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

/**
 * Delete a user
 */
export const deleteUser = async (
  userId: string,
  token: string,
): Promise<void> => {
  await axios.delete(`${getCmsApiBaseUrl()}/user/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Assign organizations to a user
 */
export const assignOrganizationsToUser = async (
  userId: string,
  organizationIds: string[],
  token: string,
): Promise<void> => {
  await axios.post(
    `${API_ENDPOINTS.ASSIGN_ORGANIZATIONS}`,
    {
      user_id: userId,
      organization_ids: organizationIds,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Remove organization from a user
 */
export const removeOrganizationFromUser = async (
  userId: string,
  organizationIds: string[],
  token: string,
): Promise<void> => {
  await axios.post(
    `${API_ENDPOINTS.REMOVE_ORGANIZATIONS}`,
    {
      user_id: userId,
      organization_ids: organizationIds,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Update user information
 */
export const updateUser = async (
  userId: string,
  data: {
    email?: string;
    password?: string;
    given_name?: string;
    family_name?: string;
  },
  token: string,
): Promise<User> => {
  // URL encode the userId to handle special characters like pipe (|)
  const encodedUserId = encodeURIComponent(userId);

  const response = await axios.put(
    `${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  return response.data;
};

/**
 * Update current user's profile (supports file uploads)
 */
export const updateProfile = async (
  data: {
    email?: string;
    password?: string;
    given_name?: string;
    family_name?: string;
    image?: File;
  },
  token: string,
  liveData?: boolean,
): Promise<User> => {
  const formData = new FormData();

  if (data.email) {
    formData.append('email', data.email);
  }
  if (data.password) {
    formData.append('password', data.password);
  }
  if (data.given_name) {
    formData.append('given_name', data.given_name);
  }
  if (data.family_name) {
    formData.append('family_name', data.family_name);
  }
  if (data.image) {
    formData.append('image', data.image);
  }

  const response = await axios.put(
    `${getCmsApiBaseUrl(liveData)}/profile`,
    formData,
    {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  },
  );

  return response.data;
};

/**
 * Assign roles to a user
 */
export const assignRolesToUser = async (
  userId: string,
  roleIds: string[],
  token: string,
): Promise<void> => {
  // URL encode the userId to handle special characters like pipe (|)
  const encodedUserId = encodeURIComponent(userId);

  await axios.post(
    `${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}/roles`,
    { roles: roleIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Remove roles from a user
 */
export const removeRolesFromUser = async (
  userId: string,
  roleIds: string[],
  token: string,
): Promise<void> => {
  // URL encode the userId to handle special characters like pipe (|)
  const encodedUserId = encodeURIComponent(userId);

  await axios.delete(`${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}/roles`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    data: { roles: roleIds },
  });
};

/**
 * Assign permissions to a user
 */
export const assignPermissionsToUser = async (
  userId: string,
  permissionValues: string[],
  token: string,
): Promise<void> => {
  // URL encode the userId to handle special characters like pipe (|)
  const encodedUserId = encodeURIComponent(userId);

  await axios.post(
    `${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}/permissions`,
    { permissions: permissionValues },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Assign flow IDs to a user (replaces the full list)
 * PUT /api/v1/user/<userId>  { flow_ids: [...] }
 */
export const updateUserFlowIds = async (
  userId: string,
  flowIds: string[],
  token: string,
): Promise<void> => {
  const encodedUserId = encodeURIComponent(userId);
  await axios.put(
    `${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}`,
    { flow_ids: flowIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Remove permissions from a user
 */
export const removePermissionsFromUser = async (
  userId: string,
  permissionValues: string[],
  token: string,
): Promise<void> => {
  // URL encode the userId to handle special characters like pipe (|)
  const encodedUserId = encodeURIComponent(userId);

  await axios.delete(
    `${API_ENDPOINTS.UPDATE_USER}/${encodedUserId}/permissions`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: { permissions: permissionValues },
    },
  );
};
